import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { samplePages } from '../data/content';
import type { SamplePage } from '../types/content';
export function PageGallery() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState<SamplePage | null>(null);
  const scrollToIndex = useCallback((next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.min(Math.max(next, 0), samplePages.length - 1);
    const card = track.children[clamped] as HTMLElement | undefined;
    if (!card) return;
    track.scrollTo({
      left: card.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
    setIndex(clamped);
  }, []);

  // Keep the indicator honest when the visitor swipes instead of using the
  // controls: read the nearest card back out of the scroll position.
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.children) as HTMLElement[];
    const closest = cards.reduce((best, card, cardIndex) => {
      const distance = Math.abs(card.offsetLeft - track.offsetLeft - track.scrollLeft);
      return distance < best.distance ? {
        distance,
        cardIndex
      } : best;
    }, {
      distance: Infinity,
      cardIndex: 0
    });
    setIndex(closest.cardIndex);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollToIndex(index + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollToIndex(index - 1);
    }
  };
  return <section aria-labelledby="pages-heading" className="mt-14" data-id="element-133" data-mp="38ead530bjkw9">
      <div className="mx-auto max-w-[640px] text-center" data-id="element-134" data-mp="snxmpf64xe89">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-gold-500" data-id="element-135" data-mp="smcrrofwrt9v">
          Sample pages from My First Builds
        </p>
        <h3 id="pages-heading" className="mt-2 font-display text-[24px] font-semibold leading-[1.25] tracking-[-0.01em] text-ink-900 md:text-[27px]" data-id="element-136" data-mp="3hhm0via41c5i">
          See the steps before you start
        </h3>
        <p className="mt-2.5 text-[15.5px] leading-[1.6] text-ink-500" data-id="element-137" data-mp="3qj2avox0jc97">
          Every project opens with the materials, then illustrated numbered steps, and closes with a
          prompt to change one thing and try it again.
        </p>
      </div>

      <div className="mt-7 flex items-center justify-between gap-4 px-1" data-id="element-138" data-mp="32s6eeehm3svm">
        <p className="text-[13px] text-ink-500" data-id="element-139" data-mp="32q3p5woaa3ry">
          Page {index + 1} of {samplePages.length}
        </p>
        <div className="flex gap-2" data-id="element-140" data-mp="37pg9oaromyrd">
          <GalleryControl label="Previous page" onClick={() => scrollToIndex(index - 1)} disabled={index === 0} data-id="element-141" data-mp="3ci9e0xj8o52x">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-142" data-mp="2f0rfkvp1ntp4" />
          </GalleryControl>
          <GalleryControl label="Next page" onClick={() => scrollToIndex(index + 1)} disabled={index === samplePages.length - 1} data-id="element-143" data-mp="2jzc8sb5vpd42">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-144" data-mp="27jw0h93e936k" />
          </GalleryControl>
        </div>
      </div>

      <div ref={trackRef} onScroll={handleScroll} onKeyDown={handleKeyDown} role="group" aria-label="Sample pages from Book 01, My First Builds" tabIndex={0} className="no-scrollbar mt-4 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" data-id="element-145" data-mp="22luk4vxcrhys">
        {samplePages.map(page => <figure key={page.image} className="w-[clamp(250px,76vw,340px)] shrink-0 snap-start" data-id="element-146" data-mp="1eufa0gzc3wcz">
            <button type="button" onClick={() => setZoomed(page)} className="block w-full overflow-hidden rounded-xl border border-cream-300 bg-white shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" aria-label={`Open a larger view of ${page.project}, ${page.page}`} data-id="element-147" data-mp="1arsfg8ba3xc3">
              <img src={page.image} alt={`${page.project}, ${page.page} of Book 01 — materials, an adult-helper note and illustrated numbered steps`} className="block w-full" width={944} height={1223} loading="lazy" decoding="async" data-id="element-148" data-mp="2w6fae9o5l4u7" />
            </button>
            <figcaption className="mt-2.5 text-[13.5px] text-ink-500" data-id="element-149" data-mp="2r4pzcu0xkr64">
              <span className="font-semibold text-ink-900" data-id="element-150" data-mp="2d8w072pylg67">{page.project}</span> · {page.page}
            </figcaption>
          </figure>)}
      </div>

      {zoomed ? <PageLightbox page={zoomed} onClose={() => setZoomed(null)} data-id="element-151" data-mp="bfomfxpzv6mf" /> : null}
    </section>;
}
type GalleryControlProps = {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
};
function GalleryControl({
  label,
  onClick,
  disabled,
  children
}: GalleryControlProps) {
  return <button type="button" onClick={onClick} disabled={disabled} aria-label={label} className="flex h-11 w-11 items-center justify-center rounded-full border border-cream-300 bg-white text-ink-900 transition-colors hover:border-ink-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-40" data-id="element-152" data-mp="lky21840mcyv">
      {children}
    </button>;
}
function PageLightbox({
  page,
  onClose
}: {
  page: SamplePage;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);
  return <div role="dialog" aria-modal="true" aria-label={`${page.project}, ${page.page}`} className="fixed inset-0 z-50 flex flex-col bg-ink-900/85 p-4" onClick={onClose} data-id="element-153" data-mp="1nz5kk1ztekyq">
      <div className="flex shrink-0 justify-end" data-id="element-154" data-mp="1xxbn8ltg3v4n">
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close the larger page view" className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sun-400" data-id="element-155" data-mp="21yeysw4gn502">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-156" data-mp="2gnb44lf4u3wz" />
        </button>
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-auto" onClick={event => event.stopPropagation()} data-id="element-157" data-mp="2lmo2o3fd8zfe">
        <img src={page.image} alt={`${page.project}, ${page.page} of Book 01, shown larger`} className="mx-auto block w-full max-w-[640px] rounded-lg bg-white" data-id="element-158" data-mp="2pn7xv1gdpsyw" />
      </div>

      <p className="mt-3 shrink-0 text-center text-[13px] text-white/80" data-id="element-159" data-mp="9yj5ueald21n">
        {page.project} · {page.page} · My First Builds
      </p>
    </div>;
}